import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Relationship : famille "lien entre deux contacts".
 *
 * Lien typé et daté entre deux contacts, avec un rôle qui précise "qui est quoi
 * pour qui" (parent, tuteur, employeur, médecin référent...). Construite sur le
 * modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas de lien concret ("parent d'élève", "tuteur légal") :
 * il connaît la famille générique `relationship`. Le vocabulaire métier est
 * porté par le champ `type` (chaîne libre déclarée par l'app), et le rôle précis
 * du lien par `role`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `fromContactId` et `toContactId` sont le CŒUR de la famille (un lien relie
 * toujours deux contacts), donc requis ; `groupId` et `consentId` sont
 * optionnels.
 *
 * Sécurité physique : les `permissions` (récupérer, facturer, prévenir) NE sont
 * PAS dans ce document v0. L'écriture d'une permission type "récupérer un
 * enfant" sera réservée aux rôles direction, journalisée et adossée à un consent
 * de preuve : elle vivra dans un sous-document privé (cf. WIP), hors v0.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille relationship (hors champs standard du socle). */
export const relationshipCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "familial", "professionnel", "administratif"). */
  type: z.string().min(1),
  /** Contact d'où part le lien (référence `contact`, cœur de la famille). */
  fromContactId: z.string().min(1),
  /** Contact vers qui va le lien (référence `contact`, cœur de la famille). */
  toContactId: z.string().min(1),
  /** Rôle précis du lien, chaîne libre pilotée par l'app (ex. "parent", "tuteur", "employeur"). Optionnel. */
  role: z.string().min(1).optional(),
  /** Début de validité du lien, optionnel. */
  startAt: z.date().optional(),
  /** Fin de validité du lien, optionnel. */
  endAt: z.date().optional(),
  /** Le contact source est-il un contact d'urgence à prévenir, optionnel. */
  isEmergencyContact: z.boolean().optional(),
  /** Groupe de rattachement (référence `group`), optionnel. */
  groupId: z.string().min(1).optional(),
  /** Consent de preuve rattaché (référence `consent`), optionnel. */
  consentId: z.string().min(1).optional(),
  // permissions (récupérer, facturer, prévenir) : sensible, ira dans un sous-document privé (cf. WIP), hors v0.
} as const;

/** Schéma complet de la famille relationship (champs standard + champs métier). */
export const RelationshipSchema = z.object({
  ...baseEntityFields,
  ...relationshipCoreFields,
});

/** Type d'une relationship. */
export type Relationship = z.infer<typeof RelationshipSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const RelationshipCreateInputSchema = z.object({
  ...relationshipCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une relationship. */
export type RelationshipCreateInput = z.infer<typeof RelationshipCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const RelationshipUpdateInputSchema = z
  .object({ ...relationshipCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une relationship. */
export type RelationshipUpdateInput = z.infer<typeof RelationshipUpdateInputSchema>;
